import {ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit,} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {FormlyFieldConfig} from '@ngx-formly/core';
import {BaseComponent, ListFilterService} from 'compob2b-libs';
import {takeUntil, tap} from 'rxjs/operators';
import {NbButtonModule, NbCheckboxModule, NbTooltipModule} from "@nebular/theme";
import {AsyncPipe} from "@angular/common";
import {TuiIcon} from '@taiga-ui/core'

@Component({
  selector: 'app-filter-panel',
  templateUrl: './filter-panel.html',
  styleUrls: ['./filter-panel.scss'],
  imports: [NbTooltipModule, NbCheckboxModule, NbButtonModule, AsyncPipe, TuiIcon],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterPanel extends BaseComponent implements OnInit, OnDestroy {
  @Input() showHasStock = false;
  @Input() filter: ListFilterService;

  /*
   * Тега категории с крестиком тут нет и не должно быть: в полосе живёт только то,
   * что лежит в query, а категория после SEO-8 §7 в ПУТИ — это идентичность
   * страницы, а не фильтр. Уход из неё обеспечивают крошки, сохраняя query.
   */

  keyword = '';
  hasStock = false;

  constructor(protected readonly route: ActivatedRoute) {
    super();
  }

  ngOnInit() {
    this.route.queryParams.pipe(tap(params => {
      this.hasStock = params.hasStock === 'true';

      if (params.keyword !== undefined && params.keyword !== null && params.keyword !== '') {
        this.keyword = params.keyword;
      } else {
        this.keyword = '';
      }

      this.cdr.detectChanges();
    }), takeUntil(this.unsubscribe),).subscribe();
  }

  delete(key, value = null) {
    const removeParams = {};

    let dateFromKey = null;
    let dateToKey = null;
    this.filter.fieldsFeatures.forEach(oneFeature => {
      if (oneFeature.key === key) {
        if (oneFeature.type === 'date-range-picker') {
          dateFromKey = key + '_from';
          dateToKey = key + '_to';
        }
      }
    });

    if (dateFromKey && dateToKey) {
      removeParams[dateFromKey] = null;
      removeParams[dateToKey] = null;

    } else if (value) {
      let newValueOfMulti = null;
      for (const [valueKey, valueItem] of Object.entries(this.route.snapshot.queryParamMap['params'])) {
        if (valueKey.toString() === key.toString()) {
          const newValue = valueItem.toString()
            .split(',')
            .filter(i => {
              let v = value.value || value;
              if (value.value === null) {
                v = "null";
              }

              return i.toString() !== v.toString()
            });

          if (newValue.length > 0) {
            newValueOfMulti = newValue.join(',');
          }
        }
      }
      removeParams[key] = newValueOfMulti;

    } else if (key === 'classId') {
      const filterExist = this.filter.fieldsFeatures
        .filter(o => {
          return (/^-?[\d.]+(?:e-?\d+)?$/.test(o.key.toString()) === false && o.key.toString() !== 'classId');
        })
        .map(props => {
          return props.key;
        })

      filterExist.push('region');

      for (const [k] of Object.entries(this.route.snapshot.queryParamMap['params'])) {
        if (!filterExist.includes(k)) {
          removeParams[k] = null;
        }
      }
    } else {
      removeParams[key] = null;
    }

    this.navigate(removeParams);
    this.cdr.detectChanges();
  }

  reset(): void {
    this.filter.reset();
    this.cdr.detectChanges();
    // Категорию НЕ переставляем в query: после переезда она в пути (SEO-8 §7), а прежний
    // `queryParams: {catalog_id: …}` оставлял в URL мёртвый параметр — его никто не читает,
    // зато он попадал в canonical и превращал страницу категории в индексируемый дубль.
    this.router.navigate(['.'], {relativeTo: this.route, queryParams: {}});
  }

  onChangeRange(value) {
    this.navigate({hasStock: value || null});
  }

  getIntervalValue(item: FormlyFieldConfig) {
    const from = item.fieldGroup[0]?.defaultValue;
    const to = item.fieldGroup[1]?.defaultValue;

    let ret = [];
    if (from) {
      ret.push('от ' + from);
    }
    if (to) {
      ret.push('до ' + to);
    }

    return ret.join(', ');
  }

  getDisplayValue(item: FormlyFieldConfig, itemMulti = null): string {
    let value = '';

    if (item.templateOptions.getDisplayValue) {
      value = item.templateOptions.getDisplayValue(item, itemMulti);
    } else {

      value = item.templateOptions.label;

      if (item.fieldGroup) {
        value += ': ' + this.getIntervalValue(item);
      } else if (item.defaultValue?.label) {
        value += `: ${item.defaultValue.label}`;
      } else if (itemMulti?.label) {
        value += `: ${itemMulti.label}`;
      } else if (itemMulti) {
        item.templateOptions?.options?.forEach(option => {
          if (option?.value == itemMulti) {
            value += `: ${option.label}`;
          }
        })
      }
    }

    return value;
  }

  /**
   * Навигация при изменении набора фильтров (включение/выключение «в наличии», снятие
   * тега). Мержит переданные параметры с текущим query.
   *
   * `page: null` — сброс пагинации на первую страницу. Без него фильтр, применённый на
   * N-й странице, оставлял `page=N`, а у отфильтрованного набора страниц МЕНЬШЕ — и
   * пользователь получал пустой список: список есть, товары есть, а страница пустая,
   * потому что запрошена страница за концом выборки. Ловилось только в браузере
   * (сценарий: категория → стр. 3 → «в наличии» → 0 товаров).
   *
   * `limit` намеренно НЕ сбрасываем: это выбранный пользователем размер страницы, а не
   * позиция в выборке.
   *
   * `preserveFragment` — потому что `queryParamsHandling: 'merge'` мержит ТОЛЬКО query, а
   * слой редиректов hash через все хопы переносит (`toNewUrl`, `resolveSlugRedirect` —
   * SEO-8), и ронять его клиентской навигацией неправильно.
   *
   * ГРАНИЦА, замеренная в браузере: до самого URL это всё равно не доезжает. Роутер
   * пушит адрес С фрагментом, а затем его же state-менеджер (`handleRouterEvent` →
   * `setBrowserUrl`) делает `replaceState` уже БЕЗ фрагмента — то есть якорь снимает
   * внутренняя пересериализация, а не эта навигация. Явный `fragment:` вместо/помимо
   * флага тоже не помогает (проверено). Флаг оставлен как верное выражение намерения:
   * стоит нуля и заработает, когда поведение роутера изменится. Deep-link с якорем при
   * этом работает — там фрагмент доносит редирект-слой и гидрация (`catalog-list`).
   */
  private navigate(queryParams) {
    this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams: {...queryParams, page: null},
      queryParamsHandling: 'merge',
      preserveFragment: true
    });
  }

}

